import React, { createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useSidebarDrawer } from '../../../hooks/useSidebarDrawer';
import './SidebarDrawer.css';

type SidebarDrawerContextValue = {
  close: () => void;
  toggle: () => void;
  isOpen: boolean;
  toggleOpenLabel: string;
  toggleCloseLabel: string;
  drawerBpClass: string;
  togglePlacement: 'fixed' | 'inline';
};

const SidebarDrawerContext = createContext<SidebarDrawerContextValue | null>(null);

/** Закрыть мобильный drawer (работает только внутри содержимого SidebarDrawer). */
export function useSidebarDrawerClose(): () => void {
  const ctx = useContext(SidebarDrawerContext);
  return ctx?.close ?? (() => {});
}

/** Кнопка drawer в разметке страницы (при togglePlacement="inline"). */
export function SidebarDrawerToggle({ className = '' }: { className?: string }) {
  const ctx = useContext(SidebarDrawerContext);
  if (!ctx || ctx.togglePlacement !== 'inline') {
    return null;
  }

  return (
    <button
      type="button"
      className={[
        'sidebar-drawer-toggle',
        'sidebar-drawer-toggle--inline',
        ctx.drawerBpClass,
        ctx.isOpen ? 'is-open' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={ctx.toggle}
      aria-expanded={ctx.isOpen}
      aria-label={ctx.isOpen ? ctx.toggleCloseLabel : ctx.toggleOpenLabel}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M9 6l6 6-6 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

export type SidebarDrawerProps = {
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
  bodyClass: string;
  toggleOpenLabel: string;
  toggleCloseLabel: string;
  panelClassName?: string;
  /** Ширина окна (px), ниже которой включается drawer. По умолчанию 1024. */
  mobileBreakpoint?: 1024 | 1400;
  /** fixed — кнопка у края экрана; inline — вставляется через SidebarDrawerToggle. */
  togglePlacement?: 'fixed' | 'inline';
  /** Обёртка flex-ряда (например client-detail-content). */
  wrapperClassName?: string;
  /** Основной контент страницы в том же контексте, что и сайдбар. */
  layout?: React.ReactNode;
  /** Блок над flex-рядом (сайдбар + layout), внутри контекста drawer — для шапки страницы. */
  pageChrome?: React.ReactNode;
  /** Класс оболочки pageChrome + flex-ряда (например calendar-page-shell). */
  pageShellClassName?: string;
  /** @deprecated Используйте layout */
  withProviderScope?: React.ReactNode;
};

const SidebarDrawer: React.FC<SidebarDrawerProps> = ({
  children,
  bodyClass,
  toggleOpenLabel,
  toggleCloseLabel,
  panelClassName = '',
  mobileBreakpoint = 1024,
  togglePlacement = 'fixed',
  wrapperClassName,
  layout,
  pageChrome,
  pageShellClassName,
  withProviderScope,
}) => {
  const pageLayout = layout ?? withProviderScope;
  const { isOpen, close, toggle } = useSidebarDrawer(bodyClass);
  const content = typeof children === 'function' ? children(close) : children;
  const hostClass =
    mobileBreakpoint === 1400 ? 'sidebar-drawer-host sidebar-drawer-host--bp-1400' : 'sidebar-drawer-host';
  const drawerBpClass = mobileBreakpoint === 1400 ? 'sidebar-drawer--bp-1400' : '';

  const contextValue: SidebarDrawerContextValue = {
    close,
    toggle,
    isOpen,
    toggleOpenLabel,
    toggleCloseLabel,
    drawerBpClass,
    togglePlacement,
  };

  const toggleButton = (
    <button
      type="button"
      className={`sidebar-drawer-toggle ${drawerBpClass}${isOpen ? ' is-open' : ''}`.trim()}
      onClick={toggle}
      aria-expanded={isOpen}
      aria-label={isOpen ? toggleCloseLabel : toggleOpenLabel}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M9 6l6 6-6 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );

  const sidebarHost = (
    <div className={hostClass}>
      <div className="sidebar-drawer-desktop-wrap">{content}</div>
    </div>
  );

  const contentRow =
    pageLayout != null ? (
      <div className={wrapperClassName ?? 'sidebar-drawer-page-layout'}>
        {sidebarHost}
        {pageLayout}
      </div>
    ) : (
      <div className={wrapperClassName}>{sidebarHost}</div>
    );

  return (
    <SidebarDrawerContext.Provider value={contextValue}>
      {pageChrome != null ? (
        <div className={pageShellClassName ?? 'sidebar-drawer-page-shell'}>
          {pageChrome}
          {contentRow}
        </div>
      ) : (
        contentRow
      )}
      {createPortal(
        <>
          {togglePlacement === 'fixed' ? toggleButton : null}
          <div className={`sidebar-drawer ${drawerBpClass}${isOpen ? ' is-open' : ''}`.trim()} aria-hidden={!isOpen}>
            <button
              type="button"
              className="sidebar-drawer-overlay"
              onClick={close}
              aria-label={toggleCloseLabel}
              tabIndex={isOpen ? 0 : -1}
            />
            <div className={`sidebar-drawer-panel ${panelClassName}`.trim()}>{content}</div>
          </div>
        </>,
        document.body,
      )}
    </SidebarDrawerContext.Provider>
  );
};

export default SidebarDrawer;
